import React, { useState } from 'react';
import api from '../services/api';
import {
  Upload, FileSpreadsheet, CheckCircle, AlertTriangle,
  X, Loader, Eye, ChevronDown, Download, Wrench
} from 'lucide-react';

const UPDATABLE_FIELDS = [
  { value: 'expiry_date', label: 'Expiry Date' },
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'cnic', label: 'CNIC' },
  { value: 'address', label: 'Address' },
  { value: 'package', label: 'Package' },
  { value: 'area', label: 'Area' },
  { value: 'status', label: 'Status' },
  { value: 'due_balance', label: 'Due Balance' },
  { value: 'credit_balance', label: 'Credit Balance' },
];

const ImportTool = () => {
  const [file, setFile] = useState(null);
  const [provider, setProvider] = useState('');
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState([]);
  const [pkgMap, setPkgMap] = useState({});
  const [areaMap, setAreaMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  // Update mode state
  const [updateMode, setUpdateMode] = useState(false);
  const [updateFields, setUpdateFields] = useState(['expiry_date']);

  const reset = () => {
    setFile(null);
    setProvider('');
    setPreview(null);
    setStats(null);
    setErrors([]);
    setPkgMap({});
    setAreaMap({});
    setResult(null);
    setShowErrors(false);
    setUpdateMode(false);
    setUpdateFields(['expiry_date']);
  };

  const toggleUpdateField = (field) => {
    setUpdateFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  // Compute counts directly from preview data (not from stats object)
  const existingCount = preview ? preview.filter(r => r._exists).length : 0;
  const newCount = preview ? preview.filter(r => !r._exists).length : 0;
  const pkgMatchedCount = preview ? preview.filter(r => r._package_matched).length : 0;

  // Filter warnings: hide package warnings when in update mode
  const displayErrors = updateMode
    ? errors.filter(e => !e.toLowerCase().includes('unmatched package'))
    : errors;

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setPreview(null);
    setStats(null);
    setErrors([]);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    if (provider) formData.append('provider', provider);

    try {
      const res = await api.post('/admin/import-customers/preview/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data.preview || []);
      setStats(res.data.stats || {});
      setErrors(res.data.errors || []);
      setPkgMap(res.data.packages || {});
      setAreaMap(res.data.areas || {});
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setErrors([`Upload failed: ${msg}`]);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview || preview.length === 0) return;

    let validRows;
    if (updateMode) {
      // In update mode, only send rows that already exist in DB
      validRows = preview.filter(r => r._exists);
    } else {
      validRows = preview.filter(r => r._package_matched && !r._exists);
    }

    if (validRows.length === 0) {
      setResult({ imported: 0, updated: 0, skipped: 0, errors: ['No valid rows to process.'] });
      return;
    }

    setImporting(true);
    try {
      const payload = {
        rows: validRows.map(r => ({
          username: r.username,
          name: r.name,
          phone: r.phone,
          cnic: r.cnic,
          address: r.address,
          package: r.package,
          area: r.area,
          status: r.status,
          expiry_date: r.expiry_date,
          created_at: r.created_at,
        })),
        update_mode: updateMode,
        update_fields: updateMode ? updateFields : [],
      };
      const res = await api.post('/admin/import-customers/confirm/', payload);
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setResult({ imported: 0, skipped: 0, errors: [`Import failed: ${msg}`] });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Upload Section */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Upload size={20} className="text-blue-600" />
          Import Customers from File
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* File Upload */}
          <div className="md:col-span-2">
            <div
              onClick={() => document.getElementById('importFile').click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet size={20} className="text-emerald-600" />
                  <span className="font-medium text-gray-700">{file.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="text-red-400 hover:text-red-600 ml-2"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-600 font-medium text-sm">Click to upload CSV or Excel</p>
                  <p className="text-xs text-gray-400 mt-1">Connect, Zong, Fibrish billing exports</p>
                </>
              )}
              <input
                id="importFile"
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>
          </div>

          {/* Provider + Update Mode + Action */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider (optional)</label>
              <div className="relative">
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="w-full appearance-none border border-gray-200 rounded-lg px-3 py-2.5 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Auto-detect from filename</option>
                  <option value="connect">Connect</option>
                  <option value="zong">Zong</option>
                  <option value="fibrish">Fibrish</option>
                  <option value="ascon">Ascon</option>
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Update Mode Toggle */}
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={updateMode}
                  onChange={(e) => setUpdateMode(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Update Existing Customers</span>
              </label>
              {updateMode && (
                <div className="mt-3 space-y-2">
                  <p className="text-[11px] text-gray-500">Select fields to update on matching usernames:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {UPDATABLE_FIELDS.map(f => (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => toggleUpdateField(f.value)}
                        className={`px-2 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          updateFields.includes(f.value)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  {updateFields.length === 0 && (
                    <p className="text-[11px] text-red-500">Select at least one field</p>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader size={16} className="animate-spin" /> Processing…</>
              ) : (
                <><Eye size={16} /> Preview</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Errors / Warnings */}
      {displayErrors.length > 0 && (
        <div className="card p-4">
          <button
            onClick={() => setShowErrors(!showErrors)}
            className="flex items-center gap-2 text-sm font-medium text-amber-700"
          >
            <AlertTriangle size={16} />
            {displayErrors.length} warning(s) / info message(s)
            <span className="text-xs text-amber-500">({showErrors ? 'hide' : 'show'})</span>
          </button>
          {showErrors && (
            <ul className="mt-3 space-y-1 text-xs text-amber-800 max-h-40 overflow-y-auto">
              {displayErrors.map((err, i) => (
                <li key={i} className="pl-4 border-l-2 border-amber-300">{err}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Stats + Actions */}
      {preview && preview.length > 0 && !result && (
        <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
            <div>
              <span className="text-gray-500">Total:</span>{' '}
              <span className="font-semibold text-gray-900">{preview.length}</span>
            </div>
            {!updateMode && (
              <div>
                <span className="text-gray-500">New:</span>{' '}
                <span className="font-semibold text-emerald-600">{newCount}</span>
              </div>
            )}
            {updateMode && (
              <div>
                <span className="text-gray-500">Existing:</span>{' '}
                <span className="font-semibold text-blue-600">{existingCount}</span>
              </div>
            )}
            {!updateMode && pkgMatchedCount < preview.length && (
              <div>
                <span className="text-gray-500">Pkg matched:</span>{' '}
                <span className="font-semibold text-emerald-600">{pkgMatchedCount}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={reset} className="btn-secondary text-sm">Cancel</button>
            <button
              onClick={handleImport}
              disabled={importing || (updateMode ? existingCount === 0 || updateFields.length === 0 : newCount === 0)}
              className={`px-6 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 text-white ${
                updateMode
                  ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
                  : 'bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300'
              }`}
            >
              {importing ? (
                <><Loader size={16} className="animate-spin" /> {updateMode ? 'Updating…' : 'Importing…'}</>
              ) : updateMode ? (
                <><Download size={16} /> Update {existingCount} Customers ({updateFields.length} field{updateFields.length !== 1 ? 's' : ''})</>
              ) : (
                <><Download size={16} /> Import {newCount} Customers</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`card p-4 ${
          (result.errors?.length || 0) === 0
            ? 'bg-emerald-50 border border-emerald-200'
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-start gap-2">
            <CheckCircle size={18} className="text-emerald-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">
                {result.imported > 0 && `${result.imported} imported`}
                {result.updated > 0 && `${result.updated} updated`}
                {result.imported > 0 || result.updated > 0 ? ', ' : ''}
                {result.skipped} skipped
              </p>
              {result.errors?.length > 0 && (
                <ul className="mt-2 text-xs text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              <button onClick={reset} className="mt-3 text-sm text-blue-600 hover:underline">
                Import another file
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Table */}
      {preview && preview.length > 0 && !result && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              Preview — {preview.length} rows
            </p>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Username</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Phone</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Package</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Area</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Expiry</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Pkg?</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600">In DB?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preview.map((row, idx) => (
                  <tr key={idx} className={`hover:bg-gray-50 ${row._exists && updateMode ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-3 py-2 font-mono text-gray-700">{row.username}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-[150px] truncate" title={row.name}>{row.name}</td>
                    <td className="px-3 py-2 text-gray-600">{row.phone}</td>
                    <td className="px-3 py-2 text-gray-600">{row.package}</td>
                    <td className="px-3 py-2 text-gray-600">{row.area || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        row.status === 'active' ? 'bg-green-100 text-green-700' :
                        row.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                        row.status === 'expired' ? 'bg-yellow-100 text-yellow-700' :
                        row.status === 'suspended' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
                        row.status === 'churned' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{row.status || 'active'}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{row.expiry_date || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {row._package_matched ? (
                        <span className="text-emerald-600 font-bold">✓</span>
                      ) : (
                        <span className="text-red-500 font-bold" title={`No match for "${row.package}"`}>✗</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {row._exists ? (
                        <span className="text-blue-600 font-bold text-xs" title="Already in database">EXISTS</span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


const Tools = () => {
  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wrench size={24} className="text-blue-600" />
          Tools
        </h1>
        <p className="text-sm text-gray-500 mt-1">Import and manage your ISP data</p>
      </div>

      <ImportTool />
    </div>
  );
};

export default Tools;
